import { CREDIT_NOTE_RULES, RULE_LABELS, type CreditNoteDocument, type CreditNoteRule } from '../../types/creditNotes';
import { formatPercent, formatSalesShare } from '../../utils/creditNoteFormat';

/**
 * What the rules concluded about one document, in the width of a table cell.
 *
 * THE THREE STATES ARE NOT TWO. A document with no fired rule is either clean or unjudgeable,
 * and conflating them is the specific failure this page cannot afford: 44 documents over 24
 * months can be judged by no rule at all, and painting them "Clean" would report an unchecked
 * document as a checked one. So:
 *
 *   fired > 0                 the rules that fired, with their measured value
 *   nothing fired, nothing skipped   Clean — every rule ran and none of them objected
 *   nothing fired, everything skipped   Not assessable, with the reasons behind the tooltip
 *   nothing fired, some skipped   Clean, plus a muted count of the rules that could not run
 *
 * The last case is the common one and the easiest to get wrong. A credit note without item
 * lines is genuinely clean on DUPLICATE and genuinely unjudgeable on PRICE_DEVIATION, and the
 * cell has to hold both facts at once.
 */

interface Props {
    doc: CreditNoteDocument;
    /** German plain text per skip reason, from the server. Never invented in the page. */
    skipReasonLabels: Record<string, string>;
}

/** The measured number behind a verdict, where the rule has one worth reading in a cell. */
function firedDetail(rule: CreditNoteRule, doc: CreditNoteDocument): string {
    const s = doc.rules.signals;
    switch (rule) {
        case 'PRICE_DEVIATION':
            return s.priceDeviationPct === null ? '' : ` ${formatPercent(s.priceDeviationPct)}`;
        case 'LARGE_VS_SALES':
            return s.salesSharePct === null ? '' : ` ${formatSalesShare(s.salesSharePct)}`;
        case 'DUPLICATE':
            return s.duplicateDocumentNos.length > 0 ? ` ×${s.duplicateDocumentNos.length + 1}` : '';
        case 'QTY_NO_VALUE':
            return s.qtyNoValueLines > 0 ? ` ${s.qtyNoValueLines}` : '';
        default:
            return '';
    }
}

const FIRED_STYLE: Record<CreditNoteRule, string> = {
    NO_ITEM_LINE: 'bg-red-50 text-red-700',
    DUPLICATE: 'bg-red-50 text-red-700',
    PRICE_DEVIATION: 'bg-amber-50 text-amber-700',
    LARGE_VS_SALES: 'bg-amber-50 text-amber-700',
    QTY_NO_VALUE: 'bg-gray-100 text-gray-600',
};

export function CreditNoteFlags({ doc, skipReasonLabels }: Props) {
    const { fired, notEvaluated } = doc.rules;

    const skipTitle = notEvaluated
        .map(n => `${RULE_LABELS[n.rule] ?? n.rule}: ${skipReasonLabels[n.reason] ?? n.reason}`)
        .join('\n');

    if (fired.length > 0) {
        return (
            <span className="inline-flex flex-wrap items-center gap-1">
                {fired.map(rule => (
                    <span
                        key={rule}
                        className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${FIRED_STYLE[rule]}`}
                        title={RULE_LABELS[rule]}
                    >
                        {RULE_LABELS[rule]}{firedDetail(rule, doc)}
                    </span>
                ))}
                {notEvaluated.length > 0 && (
                    <span className="text-[11px] text-gray-400" title={skipTitle}>
                        {notEvaluated.length} n/a
                    </span>
                )}
            </span>
        );
    }

    if (notEvaluated.length >= CREDIT_NOTE_RULES.length) {
        return (
            <span
                className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600"
                title={skipTitle}
            >
                Not assessable
            </span>
        );
    }

    return (
        <span className="inline-flex items-center gap-1">
            <span className="inline-flex items-center rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700">
                Clean
            </span>
            {notEvaluated.length > 0 && (
                <span className="text-[11px] text-gray-400" title={skipTitle}>
                    {notEvaluated.length} n/a
                </span>
            )}
        </span>
    );
}
